import { Trash2 } from 'lucide-react'

import type { PackagingDraftItem } from '@/lib/packaging-draft'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatVND } from '@/lib/format'
import { rowKeyOf } from '@/lib/material-row-id'
import { m } from '@/lib/paraglide/messages'

interface Props {
    item: PackagingDraftItem
    onQtyChange: (rowKey: string, value: string) => void
    onPriceChange: (rowKey: string, value: string) => void
    onDiscountChange: (rowKey: string, value: string) => void
    onRemove: (rowKey: string) => void
}

/**
 * Dòng vật tư nhân viên TỰ THÊM — render ngang hàng với dòng sản phẩm trong
 * bảng hàng hóa, không thụt lề như vật tư theo định mức (những dòng đó dùng
 * OrderMaterialSubRows và nằm dưới đúng sản phẩm của chúng).
 *
 * Mọi callback nhận KHOÁ DÒNG (rowKeyOf) chứ không phải material_id: cùng một
 * vật tư được thêm nhiều dòng nên material_id không phân biệt được dòng nào.
 *
 * Trả về một <tr> nên PHẢI đặt trong <tbody> của bảng cha.
 */
export function OrderMaterialRow({
    item,
    onQtyChange,
    onPriceChange,
    onDiscountChange,
    onRemove,
}: Props) {
    const rowKey = rowKeyOf(item)
    const shortage = item.quantity > item.available
    const lineAmount =
        item.unit_price *
        item.quantity *
        (1 - (item.discount_percent ?? 0) / 100)
    // CK chỉ có nghĩa khi có tiền để trừ — vật tư đơn giá 0 thì gõ bao nhiêu
    // thành tiền cũng ra 0, hiện ô nhập chỉ gây hiểu nhầm.
    const priced = item.unit_price > 0

    return (
        <tr
            className={`border-b align-middle ${shortage ? 'bg-amber-50' : ''}`}
        >
            <td className="p-2 text-sm">
                <span className="font-medium">{item.material_name}</span>
                <span className="ml-1 text-xs text-muted-foreground">
                    {m.material_row_suffix()}
                </span>
                <div className="text-xs text-muted-foreground">
                    {m.packaging_editor_col_available()}:{' '}
                    {item.available.toLocaleString('vi-VN', {
                        maximumFractionDigits: 3,
                    })}
                    {shortage ? (
                        <span className="ml-1 text-amber-700">
                            ({m.packaging_editor_badge_shortage()})
                        </span>
                    ) : null}
                </div>
            </td>
            <td className="p-2 text-sm text-muted-foreground whitespace-nowrap">
                {item.material_unit || '—'}
            </td>
            <td className="p-2 text-sm">
                <Input
                    type="number"
                    min={0}
                    step="0.001"
                    inputMode="decimal"
                    value={item.quantity}
                    onChange={(e) => onQtyChange(rowKey, e.target.value)}
                    className="w-full"
                    aria-label={`${item.material_name} SL`}
                />
            </td>
            <td className="p-2 text-sm">
                <Input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    value={item.unit_price}
                    onChange={(e) => onPriceChange(rowKey, e.target.value)}
                    className="w-full"
                    aria-label={`${item.material_name} ${m.order_unit_price()}`}
                />
            </td>
            <td className="p-2">
                {priced ? (
                    <Input
                        type="number"
                        min={0}
                        max={100}
                        step="any"
                        inputMode="decimal"
                        value={item.discount_percent}
                        onChange={(e) =>
                            onDiscountChange(rowKey, e.target.value)
                        }
                        className="w-full"
                        aria-label={`${item.material_name} CK %`}
                    />
                ) : (
                    <span className="pl-2.5 text-muted-foreground">—</span>
                )}
            </td>
            <td className="p-2 text-right text-sm font-medium whitespace-nowrap">
                {formatVND(lineAmount)}
            </td>
            <td className="p-2">
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => onRemove(rowKey)}
                    className="text-destructive"
                    aria-label={`${m.common_aria_delete()} ${item.material_name}`}
                >
                    <Trash2 className="size-4" />
                </Button>
            </td>
        </tr>
    )
}
